/**
 * Transform functions for Dental HMO (DHMO) benefits
 *
 * Handles bi-directional transformation between:
 * - API format (dhmoBenefits array with adaCode/serviceName/memberCost objects)
 * - Grid format (flat fields for each ADA code's memberCost)
 */

import { shouldIncludeBenefitInApi } from "../shared/transforms/builders";
import { ServiceWaitingPeriod } from "../../types/rfp.api.types";

import { dentalHmoBenefitDefinitions } from "./definitions";
import { DENTAL_HMO_VALID_FIELDS } from "./validFields";

import type {
  DentalExtendedBenefitApi,
  DentalHmoBenefitApi,
  DentalHmoBenefitGrid,
  DhmoBenefitDetail,
} from "./types";

/**
 * Mapping of grid field names to ADA code information
 * This ensures consistency between grid fields and API structure
 */
const ADA_CODE_MAP: Record<string, { adaCode: string; serviceName: string }> = {
  adaCodeD0120: {
    adaCode: "D0120",
    serviceName: "Clinical Oral Evaluation - Periodic oral evaluation",
  },
  adaCodeD0150: {
    adaCode: "D0150",
    serviceName: "Clinical Oral Evaluation - Comprehensive oral evaluation",
  },
  adaCodeD1110: {
    adaCode: "D1110",
    serviceName: "Prophylaxis - Adult",
  },
  adaCodeD1120: {
    adaCode: "D1120",
    serviceName: "Prophylaxis - Child",
  },
  adaCodeD1206: {
    adaCode: "D1206",
    serviceName: "Topical Fluoride - Topical application (w/o prophy) - child",
  },
  adaCodeD1351: {
    adaCode: "D1351",
    serviceName: "Sealant Per Tooth Molars",
  },
  adaCodeD2140: {
    adaCode: "D2140",
    serviceName: "Amalgam Restorations - one surface, primary/permanent",
  },
  adaCodeD2150: {
    adaCode: "D2150",
    serviceName: "Amalgam Restorations - two surfaces, primary/permanent",
  },
  adaCodeD2160: {
    adaCode: "D2160",
    serviceName: "Amalgam Restorations - three surfaces, primary/ permanent",
  },
  adaCodeD2161: {
    adaCode: "D2161",
    serviceName: "Amalgam Restorations - four +surfaces, primary/permanent",
  },
  adaCodeD2751: {
    adaCode: "D2751",
    serviceName: "Crowns - Porcelain fused to predominantly base metal",
  },
  adaCodeD2752: {
    adaCode: "D2752",
    serviceName: "Crowns - Porcelain fused to noble metal",
  },
  adaCodeD3310: {
    adaCode: "D3310",
    serviceName: "Root Canal Therapy - Anterior (excluding final restoration)",
  },
  adaCodeD3320: {
    adaCode: "D3320",
    serviceName: "Root Canal Therapy - Bicuspid (excluding final restoration)",
  },
  adaCodeD3330: {
    adaCode: "D3330",
    serviceName: "Root Canal Therapy - Molar (excluding final restoration)",
  },
  adaCodeD3410: {
    adaCode: "D3410",
    serviceName: "Apicoectomy/Periradicular Surgery - Anterior",
  },
  adaCodeD4210: {
    adaCode: "D4210",
    serviceName: "Gingivectomy - 4+ Teeth/Quad",
  },
  adaCodeD4341: {
    adaCode: "D4341",
    serviceName: "Non-Surgical Periodontal - Scaling/root planing - four +",
  },
  adaCodeD4910: {
    adaCode: "D4910",
    serviceName: "Non-Surgical Periodontal - Periodontal maintenance",
  },
  adaCodeD5110: {
    adaCode: "D5110",
    serviceName: "Complete Dentures - Complete denture - upper",
  },
  adaCodeD5120: {
    adaCode: "D5120",
    serviceName: "Complete Dentures - Complete denture - lower",
  },
  adaCodeD5213: {
    adaCode: "D5213",
    serviceName: "Partial Dentures - Upper w/ metal base, resin saddles, etc",
  },
  adaCodeD7111: {
    adaCode: "D7111",
    serviceName: "Extraction, coronal remnants - deciduous tooth",
  },
  adaCodeD7140: {
    adaCode: "D7140",
    serviceName: "Extractions - Erupted tooth or exposed root",
  },
  adaCodeD7210: {
    adaCode: "D7210",
    serviceName: "Surgical Extractions - extraction of tooth; erupted",
  },
  adaCodeD8080: {
    adaCode: "D8080",
    serviceName: "Orthodontia -Treatment (Child)",
  },
  adaCodeD8090: {
    adaCode: "D8090",
    serviceName: "Orthodontia -Treatment (Adult)",
  },
};

/**
 * Helper to check if a numeric value is valid (>= 0 for dollar amounts)
 */
const isValidAmount = (value: unknown): boolean =>
  value !== undefined && value !== null && (value as number) >= 0;

/**
 * Transforms DHMO benefits from grid format to API format
 *
 * @param gridBenefits - Benefits in flat grid format
 * @returns Benefits in nested API format with dhmoBenefits array
 */
export const transformDentalHmoBenefitsToApi = (
  gridBenefits: Partial<DentalHmoBenefitGrid>,
): DentalHmoBenefitApi => {
  const transformed: Partial<DentalHmoBenefitApi> = {};

  // Build dhmoBenefits array from flat ADA code fields
  const dhmoBenefits: DhmoBenefitDetail[] = [];

  for (const [fieldName, config] of Object.entries(ADA_CODE_MAP)) {
    const memberCost = (gridBenefits as Record<string, unknown>)[fieldName];

    // Only include if valid amount (>= 0)
    if (isValidAmount(memberCost)) {
      dhmoBenefits.push({
        adaCode: config.adaCode,
        serviceName: config.serviceName,
        memberCost: memberCost as number,
      });
    }
  }

  // Only add dhmoBenefits if there are entries
  if (dhmoBenefits.length > 0) {
    transformed.dhmoBenefits = dhmoBenefits;
  }

  // Keep service waiting periods synced
  if (Array.isArray(gridBenefits.serviceWaitingPeriods)) {
    transformed.serviceWaitingPeriods = gridBenefits.serviceWaitingPeriods;
  } else if (Array.isArray(gridBenefits.serviceWaitingPeriodsMonths)) {
    transformed.serviceWaitingPeriods =
      gridBenefits.serviceWaitingPeriodsMonths;
  }

  // Simple field mappings
  if (shouldIncludeBenefitInApi(gridBenefits.annualOpenEnrollment)) {
    transformed.annualOpenEnrollment = gridBenefits.annualOpenEnrollment;
  }
  if (shouldIncludeBenefitInApi(gridBenefits.employerContribution)) {
    transformed.employerContribution = gridBenefits.employerContribution;
  }

  // Extended (non-v5) fields — CD-1659/CD-1660. Sent as a full object (a
  // full-replace PATCH, like the rest of `benefits`) whenever at least one
  // extended field is set, so an edit to any other field on the plan
  // doesn't drop the extended values already on it. Omitted entirely when
  // every extended field is unset, so an edit that never touches these
  // fields doesn't create an empty extended row on the backend.
  const extended: DentalExtendedBenefitApi = {
    preventativeAdvantage: gridBenefits.preventativeAdvantage,
    oralExams: gridBenefits.oralExams,
    oralExamFrequency: gridBenefits.oralExamFrequency,
    cleaningFrequency: gridBenefits.cleaningFrequency,
    bitewingXRays: gridBenefits.bitewingXRays,
    fullMouthXRays: gridBenefits.fullMouthXRays,
    fullMouthXRaysFrequency: gridBenefits.fullMouthXRaysFrequency,
    fluorideTreatments: gridBenefits.fluorideTreatments,
    amalgamSilverFillings: gridBenefits.amalgamSilverFillings,
    simpleExtractions: gridBenefits.simpleExtractions,
    bridgesAndDentures: gridBenefits.bridgesDentures,
    bridgesAndDenturesFrequency: gridBenefits.bridgesDenturesFrequency,
    crowns: gridBenefits.crowns,
    crownsFrequency: gridBenefits.crownsFrequency,
    tmj: gridBenefits.tmj,
    workInProgressIncluded: gridBenefits.workInProgressIncluded,
    openOrClosedContract: gridBenefits.openOrClosedContract,
    eligibilityHours: gridBenefits.eligibilityHours,
    weightedAvgOutOfPocket: gridBenefits.weightedAvgOutOfPocket,
  };
  if (
    Object.values(extended).some((value) => value !== undefined && value !== "")
  ) {
    transformed.extended = extended;
  }

  // Add objectType and filter to valid fields
  const withObjectType: DentalHmoBenefitApi = {
    ...transformed,
    objectType: "dental",
  };

  // Filter to only valid DHMO fields
  const filtered: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(withObjectType)) {
    if (DENTAL_HMO_VALID_FIELDS.has(key)) {
      filtered[key] = value;
    }
  }

  return filtered as unknown as DentalHmoBenefitApi;
};

/**
 * Transforms DHMO benefits from API format to grid format
 *
 * @param apiBenefits - Benefits in nested API format
 * @returns Benefits in flat grid format
 */
export const transformDentalHmoBenefitsFromApi = (
  apiBenefits: Partial<DentalHmoBenefitApi>,
): DentalHmoBenefitGrid => {
  const transformed: Partial<DentalHmoBenefitGrid> = {
    objectType: "dental",
  };

  // Transform dhmoBenefits array to flat ADA code fields
  if (Array.isArray(apiBenefits.dhmoBenefits)) {
    for (const benefit of apiBenefits.dhmoBenefits) {
      // Find the matching field name for this ADA code
      const fieldName = Object.keys(ADA_CODE_MAP).find(
        (key) => ADA_CODE_MAP[key].adaCode === benefit.adaCode,
      );

      if (fieldName && isValidAmount(benefit.memberCost)) {
        (transformed as Record<string, unknown>)[fieldName] =
          benefit.memberCost;
      }
    }
  }

  // Keep service waiting periods synced — filter out incomplete partial entries
  if (Array.isArray(apiBenefits.serviceWaitingPeriods)) {
    const completeWaitingPeriods = apiBenefits.serviceWaitingPeriods.filter(
      (wp): wp is ServiceWaitingPeriod =>
        wp.type !== undefined && wp.months !== undefined,
    );
    transformed.serviceWaitingPeriods = completeWaitingPeriods;
    transformed.serviceWaitingPeriodsMonths = completeWaitingPeriods;
  }

  // Simple field mappings
  if (apiBenefits.annualOpenEnrollment) {
    transformed.annualOpenEnrollment = apiBenefits.annualOpenEnrollment;
  }
  if (apiBenefits.employerContribution) {
    transformed.employerContribution = apiBenefits.employerContribution;
  }

  // Extended (non-v5) fields — CD-1659/CD-1660
  Object.assign(
    transformed,
    transformDentalHmoExtendedFromApi(apiBenefits.extended),
  );

  return transformed as DentalHmoBenefitGrid;
};

type DentalHmoExtendedGridFields = Pick<
  DentalHmoBenefitGrid,
  | "preventativeAdvantage"
  | "oralExams"
  | "oralExamFrequency"
  | "cleaningFrequency"
  | "bitewingXRays"
  | "fullMouthXRays"
  | "fullMouthXRaysFrequency"
  | "fluorideTreatments"
  | "amalgamSilverFillings"
  | "simpleExtractions"
  | "bridgesDentures"
  | "bridgesDenturesFrequency"
  | "crowns"
  | "crownsFrequency"
  | "tmj"
  | "workInProgressIncluded"
  | "openOrClosedContract"
  | "eligibilityHours"
  | "weightedAvgOutOfPocket"
>;

// Maps each grid key to its API key. Grid and API field names diverge only
// for bridgesDentures(Frequency) vs bridgesAndDentures(Frequency) — kept
// consistent with dental-ppo's grid key spelling — every other pair is
// identical.
const DENTAL_HMO_EXTENDED_FIELD_KEYS: [
  keyof DentalHmoExtendedGridFields,
  keyof DentalExtendedBenefitApi,
][] = [
  ["preventativeAdvantage", "preventativeAdvantage"],
  ["oralExams", "oralExams"],
  ["oralExamFrequency", "oralExamFrequency"],
  ["cleaningFrequency", "cleaningFrequency"],
  ["bitewingXRays", "bitewingXRays"],
  ["fullMouthXRays", "fullMouthXRays"],
  ["fullMouthXRaysFrequency", "fullMouthXRaysFrequency"],
  ["fluorideTreatments", "fluorideTreatments"],
  ["amalgamSilverFillings", "amalgamSilverFillings"],
  ["simpleExtractions", "simpleExtractions"],
  ["bridgesDentures", "bridgesAndDentures"],
  ["bridgesDenturesFrequency", "bridgesAndDenturesFrequency"],
  ["crowns", "crowns"],
  ["crownsFrequency", "crownsFrequency"],
  ["tmj", "tmj"],
  ["workInProgressIncluded", "workInProgressIncluded"],
  ["openOrClosedContract", "openOrClosedContract"],
  ["eligibilityHours", "eligibilityHours"],
  ["weightedAvgOutOfPocket", "weightedAvgOutOfPocket"],
];

// Extended (non-v5) fields — CD-1659/CD-1660. Split out of
// transformDentalHmoBenefitsFromApi to keep that function's complexity down.
// Every grid key is present (as undefined) even when `extended` itself is
// absent, so a later edit to any other field doesn't leave a stale extended
// value in the grid row from a previous plan.
const transformDentalHmoExtendedFromApi = (
  extended: DentalExtendedBenefitApi | undefined,
): DentalHmoExtendedGridFields =>
  Object.fromEntries(
    DENTAL_HMO_EXTENDED_FIELD_KEYS.map(([gridKey, apiKey]) => [
      gridKey,
      extended?.[apiKey] ?? undefined,
    ]),
  ) as DentalHmoExtendedGridFields;

/**
 * Grid keys that persist under `benefits.extended` — see
 * STD_EXTENDED_GRID_FIELD_KEYS in `@platform/btx-benefit-transforms`. Limited
 * to the keys DHMO has a row for: the transforms above round-trip the whole
 * struct shared with dental-ppo, but most of it has no cell on this LOC.
 */
export const DENTAL_HMO_EXTENDED_GRID_FIELD_KEYS: ReadonlySet<string> = new Set(
  DENTAL_HMO_EXTENDED_FIELD_KEYS.map(([gridKey]) => gridKey).filter(
    (gridKey) => gridKey in dentalHmoBenefitDefinitions,
  ),
);
